import { I18n } from '@calendly/i18n';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { ArrowDownIcon } from '@calendly/ui/components/icons';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useCallback, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { useShellCenterPanelCollapsed } from 'features/app_shell/shell_layout/useShellCenterPanelCollapsed';
import { modalVisibleSelector } from 'features/book_modal';
import { trackContactInteraction } from 'features/contacts';
import { isShareOrReschedulePopupOpenSelector } from 'features/host_reschedule';
import { useTrack } from 'hooks/analytics_events';
import { isFilterModalOpenSelector } from 'selectors/meetings/filter_modal';
import { scrollToToday } from 'shared/utils';
import { ArrowUpIcon, StyledButton } from './styles';
import { useScrollToTodayButton } from './use_scroll_to_today_button';
const buttonContainerIsTopCss = "b1gthv1t";
const buttonContainerBehindFilterCss = "bmgmve9";
const buttonContainerIsProductRefreshAppShellCss = "bswu6mo";
const buttonContainerPresetMeetingAreaTopCss = "b1tjwhet";
const buttonContainerCss = "b10rtq5k";
const relativeButtonContainerCss = "rnx5kz";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    isTop,
    useRelativePositioning = false,
    isBehindFilter = false,
    ...restProps
  } = props;
  const Container = useRelativePositioning ? 'div' : FixedContainer;
  const isProductRefreshAppShell = useProductRefreshAppShell();
  return <Container ref={ref} {...restProps} className={classNames(buttonContainerCss, {
    [buttonContainerIsTopCss]: isTop,
    [buttonContainerIsProductRefreshAppShellCss]: isProductRefreshAppShell,
    [buttonContainerBehindFilterCss]: isBehindFilter,
    [relativeButtonContainerCss]: useRelativePositioning
  }, props.className)} />;
});
const buttonContentCss = "b15wyww7";
const ButtonContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(buttonContentCss, props.className)} />;
});
const inlineTopWrapperCss = "ies3y0x";
const presetFixedTopButtonCss = "p103wyw";

/**
 * Presentational ScrollToTodayButton component.
 * This is a "dumb" component that receives all state and callbacks as props,
 * making it easy to test and use in Storybook.
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the button should be visible
 * @param {boolean} props.isTop - Whether the button should be positioned at the top
 * @param {number|undefined} props.buttonLeft - Horizontal position of the button (in pixels)
 * @param {Function} props.onClick - Callback when the button is clicked
 * @param {boolean} [props.useRelativePositioning] - Use relative positioning instead of fixed (for Storybook)
 * @param {boolean} [props.isBehindFilter] - Whether the button should appear behind the filter modal
 * @param {string} [props.className] - Optional additional className
 */
export const ScrollToTodayButtonPresenter = ({
  isVisible,
  isTop,
  buttonLeft,
  onClick,
  useRelativePositioning = false,
  isBehindFilter = false,
  inline = false,
  presetMeetingAreaTop = false,
  className,
  ...props
}) => {
  if (!isVisible) return null;
  const buttonContainerLeftCss = "b1kfe28u";
  const button = <StyledButton onClick={onClick} decoration="secondary-outline" data-testid="scroll-to-today-button" {...props}>
      <ButtonContent>
        {isTop ? <>
            <ArrowUpIcon />
            {I18n.t('publisher.meetings.scroll_to_today')}
          </> : <>
            <ArrowDownIcon />
            {I18n.t('publisher.meetings.scroll_to_today')}
          </>}
      </ButtonContent>
    </StyledButton>;
  if (inline) {
    return <div className={inlineTopWrapperCss} data-testid="scroll-to-today-inline-top">
        {button}
      </div>;
  }
  return <ButtonContainer isTop={isTop} useRelativePositioning={useRelativePositioning} isBehindFilter={isBehindFilter} style={buttonLeft !== undefined ? {
    left: buttonLeft
  } : undefined} className={classNames({
    [buttonContainerLeftCss]: buttonLeft !== undefined,
    [buttonContainerPresetMeetingAreaTopCss]: presetMeetingAreaTop && isTop
  }, className)}>
      <StyledButton onClick={onClick} decoration="secondary-outline" data-testid="scroll-to-today-button" className={presetMeetingAreaTop && isTop ? presetFixedTopButtonCss : undefined} {...props}>
        <ButtonContent>
          {isTop ? <>
              <ArrowUpIcon />
              {I18n.t('publisher.meetings.scroll_to_today')}
            </> : <>
              <ArrowDownIcon />
              {I18n.t('publisher.meetings.scroll_to_today')}
            </>}
        </ButtonContent>
      </StyledButton>
    </ButtonContainer>;
};

/**
 * Smart wrapper component that provides the default behavior.
 * This component uses the hook and handles scroll logic and analytics.
 * This is the default export for backward compatibility.
 * For Storybook, use ScrollToTodayButtonPresenter with the hook directly.
 *
 * @param {Object} props
 * @param {React.RefObject<HTMLDivElement>} props.containerRef - Ref to the scrollable container
 * @param {Array<{rawDate: string, date: string, events?: unknown[]}>} props.meetings - Array of meeting groups
 * @param {string} props.timezone - Timezone string (e.g., 'America/New_York')
 * @param {string|null} [props.contactUuid] - Optional contact UUID for analytics tracking
 * @param {moment.Moment} [props.currentTime] - Optional current time moment object for timezone-aware scrolling
 * @param {boolean} [props.presetMeetingAreaTop] - Preset meetings page: fixed top sits in the list band below sticky chrome
 */
export const SCROLL_TO_TODAY_PLACEMENT = {
  FIXED: 'fixed',
  /** @deprecated Inline list placement scrolls off-screen when today is above the viewport; use presetMeetingAreaTop */
  INLINE_TOP: 'inline-top',
  /** @deprecated Use a single fixed button with presetMeetingAreaTop instead */
  FIXED_BOTTOM_ONLY: 'fixed-bottom-only'
};
export const ScrollToTodayButton = ({
  containerRef,
  meetings,
  timezone,
  contactUuid = null,
  currentTime = null,
  placement = SCROLL_TO_TODAY_PLACEMENT.FIXED,
  presetMeetingAreaTop = false,
  isHidden = false,
  ...props
}) => {
  const track = useTrack();
  const isFilterModalOpen = useSelector(isFilterModalOpenSelector);
  const isBookModalOpen = useSelector(modalVisibleSelector);
  const isReschedulePopupOpen = useSelector(isShareOrReschedulePopupOpenSelector);
  const isCenterPanelCollapsed = useShellCenterPanelCollapsed();
  const {
    buttonLeft,
    isTop,
    isVisible
  } = useScrollToTodayButton({
    containerRef,
    meetings,
    timezone,
    currentTime
  });
  const handleScrollToToday = useCallback(() => {
    const now = currentTime || moment().tz(timezone);
    scrollToToday(containerRef, meetings, timezone, true, now);

    // Track from Contact page if contactUuid provided
    if (contactUuid) {
      trackContactInteraction({
        track,
        location: 'interactions_feed_all',
        action: 'scroll_to_today',
        contactUuid
      });
    }
  }, [containerRef, meetings, timezone, currentTime, contactUuid, track]);
  const isBehindFilter = isFilterModalOpen || isBookModalOpen || isReschedulePopupOpen;
  const presenterProps = {
    buttonLeft,
    onClick: handleScrollToToday,
    isBehindFilter,
    ...props
  };
  if (placement === SCROLL_TO_TODAY_PLACEMENT.INLINE_TOP) {
    if (!isVisible || !isTop) {
      return null;
    }
    return <ScrollToTodayButtonPresenter isVisible isTop inline {...presenterProps} />;
  }
  if (placement === SCROLL_TO_TODAY_PLACEMENT.FIXED_BOTTOM_ONLY) {
    if (!isVisible || isTop) {
      return null;
    }
    return <ScrollToTodayButtonPresenter isVisible={isVisible} isTop={false} {...presenterProps} />;
  }
  return <ScrollToTodayButtonPresenter isVisible={isVisible && !isCenterPanelCollapsed && !isHidden} isTop={isTop} presetMeetingAreaTop={presetMeetingAreaTop} {...presenterProps} />;
};

require("./scroll_to_today_button.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./scroll_to_today_button.js");